import _extends from '@babel/runtime/helpers/extends';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { getValueIf } from '../../util/getValueIf.js';
import { useEnterExitAnimation } from '../../util/useEnterExitAnimation.js';
import { useEventListener } from '../../util/useEventListener.js';
import { useLockBodyScroll } from '../../util/useLockBodyScroll.js';
import useScreenSize from '../../util/useScreenSize.js';
import useUniqueId from '../../util/useUniqueId.js';
import { Box } from '../Box/Box.js';
import { FocusTrap } from '../FocusTrap/FocusTrap.js';
import { Image as ImageMemoized } from '../Image/Image.js';
import { Typography } from '../Typography/Typography.js';
import { useThemeContext } from '../../contexts/session/Theme.js';
import { ZINDEX } from '../../static/constants.js';
import { Actions } from './Actions.js';
import { CloseButton } from './CloseButton.js';
import { actionPropsMapper, modalSizeMapper, closeButtonPropsMapper } from './Modal.mappers.js';
import { Overlay } from './Overlay.js';

var _excluded = ["visible", "onClose", "onCloseCompleted", "children", "size", "zIndex", "title", "description", "actions", "heroAsset", "closeButton", "aria-label"];

/**
 * Modal provides a temporary, focused space for users to take care of important tasks or gather more information without leaving the current page. So whether you're prompting users to make a decision or displaying additional content, modal can be useful.
 */
var Modal = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var visible = _ref.visible,
    onClose = _ref.onClose,
    onCloseCompleted = _ref.onCloseCompleted,
    children = _ref.children,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'm' : _ref$size,
    _ref$zIndex = _ref.zIndex,
    zIndex = _ref$zIndex === void 0 ? ZINDEX.FLOATING_BACKGROUND : _ref$zIndex,
    title = _ref.title,
    description = _ref.description,
    actions = _ref.actions,
    heroAsset = _ref.heroAsset,
    closeButton = _ref.closeButton,
    ariaLabel = _ref['aria-label'],
    rest = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React.useState(),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    closeSourceElement = _React$useState2[0],
    setCloseSourceElement = _React$useState2[1];
  var lockBodyScroll = useLockBodyScroll();
  var close = React.useCallback(function (source) {
    setCloseSourceElement(source);
    onClose === null || onClose === void 0 || onClose(source);
  }, [onClose]);
  var triggerOnCloseCompleted = React.useCallback(function () {
    onCloseCompleted === null || onCloseCompleted === void 0 || onCloseCompleted(closeSourceElement);
  }, [onCloseCompleted, closeSourceElement]);
  var screenSize = useScreenSize();
  var theme = useThemeContext();
  var widthSpacing = theme.spacing[16];
  var heightSpacing = size === 's' ? theme.spacing[48] : theme.spacing[64];
  var heroAssetElement = React.useMemo(function () {
    return heroAsset && /*#__PURE__*/React.createElement(Box, {
      maxHeight: "35vh",
      height: heroAsset.height,
      borderRadius: ['16', '16', '0', '0'],
      overflow: "hidden",
      mt: "-48",
      mx: "-16",
      mb: "24",
      flexShrink: 0,
      flexGrow: 1
    }, /*#__PURE__*/React.createElement(ImageMemoized, {
      src: heroAsset.src,
      alt: heroAsset.alt,
      height: "100%",
      objectFit: "cover"
    }));
  }, [heroAsset]);
  var titleId = useUniqueId();
  var header = React.useMemo(function () {
    return (title || description) && /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      flexDirection: "column"
    }, title && /*#__PURE__*/React.createElement(Typography, {
      id: titleId,
      variant: "heading-l",
      textColor: "base.default",
      mb: description ? '8' : undefined
    }, title), description && /*#__PURE__*/React.createElement(Typography, {
      variant: "body-s",
      textColor: "base.default"
    }, description));
  }, [description, title, titleId]);

  // Set negative margin with padding to cover overflow edge cases
  var scrollableChildren = /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    mx: "-8",
    px: "8",
    mb: "-4",
    pb: "4",
    flexGrow: 1,
    flexShrink: 1
  }, header, children && /*#__PURE__*/React.createElement(Box, {
    ref: ref,
    display: "flex",
    flexDirection: "column",
    mt: "16"
  }, children));

  // TODO: ADSWEB-1466 Align Modal actions internally
  var actionElements = React.useMemo(function () {
    var _actionPropsMapper = actionPropsMapper(size, screenSize),
      direction = _actionPropsMapper.direction,
      flexDirection = _actionPropsMapper.flexDirection,
      alignItems = _actionPropsMapper.alignItems;
    var resultActions = direction === 'column' ? actions : _toConsumableArray(actions || []).reverse();
    return actions && /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      flexDirection: flexDirection,
      alignItems: alignItems,
      justifyContent: "flex-end",
      mt: "16",
      mb: direction === 'column' ? '-8' : undefined
    }, /*#__PURE__*/React.createElement(Actions, {
      actions: resultActions,
      actionDirection: direction
    }));
  }, [size, screenSize, actions]);
  var _useEnterExitAnimatio = useEnterExitAnimation({
      initialProps: {
        opacity: 0
      },
      enterProps: {
        opacity: 1
      },
      animationOn: visible,
      transition: '300ms ease-in-out',
      animationFallBackTimeMs: 300
    }),
    transitionProps = _useEnterExitAnimatio.transitionProps,
    shouldRender = _useEnterExitAnimatio.shouldRender;
  var handleEscKey = React.useCallback(function (event) {
    if (event instanceof KeyboardEvent && event.key === 'Escape') {
      close('escape');
    }
  }, [close]);
  useEventListener({
    handler: handleEscKey,
    eventName: 'keydown',
    attached: shouldRender
  });
  var modal = /*#__PURE__*/React.createElement(Overlay, _extends({
    height: "100vh",
    zIndex: zIndex,
    onClick: function onClick() {
      return close('backdrop');
    }
  }, transitionProps), /*#__PURE__*/React.createElement(FocusTrap, {
    display: "flex",
    open: shouldRender,
    onClose: triggerOnCloseCompleted
  }, /*#__PURE__*/React.createElement(Box, _extends({}, rest, modalSizeMapper(size, screenSize, widthSpacing, heightSpacing), {
    ref: lockBodyScroll,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "base.surface",
    borderRadius: "16",
    boxShadow: "bottom-m",
    pt: "48",
    px: "16",
    pb: "16",
    height: "100%",
    position: "relative",
    onClick: function onClick(event) {
      return event.stopPropagation();
    },
    role: "dialog"
  }, ariaLabel ? {
    'aria-label': ariaLabel
  } : {
    'aria-label': getValueIf(!title, 'dialog'),
    'aria-labelledby': getValueIf(!!title, titleId)
  }, {
    "aria-modal": "true"
  }), heroAssetElement, /*#__PURE__*/React.createElement(CloseButton, closeButtonPropsMapper(heroAsset, function () {
    return close('close');
  }, closeButton)), scrollableChildren, actionElements)));
  return shouldRender ? modal : null;
});
Modal.defaultProps = {
  size: 'm'
};
var ModalMemoized = /*#__PURE__*/React.memo(Modal);
ModalMemoized.displayName = 'Modal';

export { ModalMemoized as Modal };
//# sourceMappingURL=Modal.js.map
